import {
  getAnalyticsAPI,
  postAnalyticsAPI,
  postOpsAnalyticsBlobAPI,
} from "../../../../api/Networking";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import {
  DailyTasksDetailResult,
  DailyTasksFilterOptions,
  DailyTasksPaginatedList,
  DailyTasksStage,
  EMPTY_FILE_WORK_SUMMARY,
  EMPTY_MOCKUP_WORK_SUMMARY,
  EMPTY_PO_WORK_SUMMARY,
  EMPTY_PR_WORK_SUMMARY,
  EMPTY_RAW_WORK_SUMMARY,
  EMPTY_PRINT_WORK_SUMMARY,
  FileWorkSummary,
  MockupWorkSummary,
  PoWorkSummary,
  PrWorkSummary,
  PrintWorkSummary,
  PrintInWorkSummary,
  EMPTY_PRINT_IN_WORK_SUMMARY,
  EMPTY_PROJECT_WORK_SUMMARY,
  ProjectWorkSummary,
  RawWorkSummary,
} from "../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  DailyTasksFilters,
  toDailyTasksQueueSearchParams,
} from "../../../../models/Dashboard/DailyTasks/DailyTasksFilters";
import {
  DailyTasksTatAnalyticsBody,
  DailyTasksTatDrilldownResult,
  DailyTasksTatMetricId,
  DailyTasksTatRecordScope,
  DailyTasksTatSort,
  DailyTasksTatSummaryResult,
} from "../../../../models/Dashboard/DailyTasks/DailyTasksTat";

export default class DailyTasksAPI {
  getFilterOptions = async (): Promise<DailyTasksFilterOptions> => {
    try {
      const response = await getAnalyticsAPI(
        "/v1/dashboards/daily-tasks/filters",
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch daily tasks filters.",
        );
      }
      return (
        (response.result as DailyTasksFilterOptions) ?? {
          order_types: [],
          sales_pocs: [],
          pm_pocs: [],
          warehouses: [],
        }
      );
    } catch (error) {
      throw error;
    }
  };

  getTatSummary = async (
    body: DailyTasksTatAnalyticsBody,
  ): Promise<DailyTasksTatSummaryResult> => {
    try {
      const response = await postAnalyticsAPI(
        "/v1/dashboards/daily-tasks/summary",
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch daily tasks TAT summary.",
        );
      }
      return response.result as DailyTasksTatSummaryResult;
    } catch (error) {
      throw error;
    }
  };

  getTatDrilldown = async (
    metricId: DailyTasksTatMetricId,
    body: DailyTasksTatAnalyticsBody,
  ): Promise<DailyTasksTatDrilldownResult> => {
    try {
      const response = await postAnalyticsAPI(
        `/v1/dashboards/daily-tasks/drilldown/${metricId}`,
        body,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch daily tasks TAT drilldown.",
        );
      }
      return response.result as DailyTasksTatDrilldownResult;
    } catch (error) {
      throw error;
    }
  };

  exportTatDrilldownCsv = async (
    metricId: DailyTasksTatMetricId,
    filters: DailyTasksTatAnalyticsBody,
    extras?: {
      record_scope?: DailyTasksTatRecordScope;
      sort?: DailyTasksTatSort;
    },
  ): Promise<APIBlobResponse> => {
    try {
      const body: DailyTasksTatAnalyticsBody = {
        ...filters,
        format: "csv",
      };
      if (extras?.record_scope) {
        body.record_scope = extras.record_scope;
      }
      if (extras?.sort) {
        body.sort = extras.sort;
      }
      return await postOpsAnalyticsBlobAPI(
        `/v1/dashboards/daily-tasks/drilldown/${metricId}`,
        body,
      );
    } catch (error) {
      throw error;
    }
  };

  getQueueSummary = async (
    filters: DailyTasksFilters,
  ): Promise<{
    mockup_work: MockupWorkSummary;
    file_work: FileWorkSummary;
    pr_work: PrWorkSummary;
    po_work: PoWorkSummary;
    raw_work: RawWorkSummary;
    print_work: PrintWorkSummary;
    print_in_work: PrintInWorkSummary;
    project_work: ProjectWorkSummary;
  }> => {
    try {
      const query = toDailyTasksQueueSearchParams(filters, {
        forSummary: true,
      }).toString();
      const response = await getAnalyticsAPI(
        `/v1/dashboards/daily-tasks/summary?${query}`,
      );
      if (!response.success) {
        throw new Error(
          response.message || "Failed to fetch daily tasks work cards.",
        );
      }
      const result = (response.result ?? {}) as {
        mockup_work?: Partial<MockupWorkSummary> | null;
        file_work?: Partial<FileWorkSummary> | null;
        pr_work?: Partial<PrWorkSummary> | null;
        po_work?: Partial<PoWorkSummary> | null;
        raw_work?: Partial<RawWorkSummary> | null;
        print_work?: Partial<PrintWorkSummary> | null;
        print_in_work?: Partial<PrintInWorkSummary> | null;
        project_work?: Partial<ProjectWorkSummary> | null;
      };
      const mockupWork = result.mockup_work;
      const fileWork = result.file_work;
      const prWork = result.pr_work;
      const poWork = result.po_work;
      const rawWork = result.raw_work;
      const printWork = result.print_work;
      const printInWork = result.print_in_work;
      const projectWork = result.project_work;
      return {
        mockup_work: {
          overdue: Number(mockupWork?.overdue ?? 0),
          today: Number(mockupWork?.today ?? 0),
          needs_upload: Number(mockupWork?.needs_upload ?? 0),
          needs_upload_today: Number(mockupWork?.needs_upload_today ?? 0),
          needs_upload_overdue: Number(mockupWork?.needs_upload_overdue ?? 0),
          changes_requested: Number(mockupWork?.changes_requested ?? 0),
          changes_requested_today: Number(
            mockupWork?.changes_requested_today ?? 0,
          ),
          changes_requested_overdue: Number(
            mockupWork?.changes_requested_overdue ?? 0,
          ),
          awaiting_approval: Number(mockupWork?.awaiting_approval ?? 0),
          awaiting_approval_today: Number(
            mockupWork?.awaiting_approval_today ?? 0,
          ),
          awaiting_approval_overdue: Number(
            mockupWork?.awaiting_approval_overdue ?? 0,
          ),
          all_pending: Number(
            mockupWork?.all_pending ?? EMPTY_MOCKUP_WORK_SUMMARY.all_pending,
          ),
        },
        file_work: {
          overdue: Number(fileWork?.overdue ?? 0),
          today: Number(fileWork?.today ?? 0),
          needs_file: Number(fileWork?.needs_file ?? 0),
          needs_file_today: Number(fileWork?.needs_file_today ?? 0),
          needs_file_overdue: Number(fileWork?.needs_file_overdue ?? 0),
          needs_send: Number(fileWork?.needs_send ?? 0),
          needs_send_today: Number(fileWork?.needs_send_today ?? 0),
          needs_send_overdue: Number(fileWork?.needs_send_overdue ?? 0),
          all_pending: Number(
            fileWork?.all_pending ?? EMPTY_FILE_WORK_SUMMARY.all_pending,
          ),
        },
        pr_work: {
          overdue: Number(prWork?.overdue ?? 0),
          today: Number(prWork?.today ?? 0),
          needs_pr: Number(prWork?.needs_pr ?? 0),
          needs_pr_today: Number(prWork?.needs_pr_today ?? 0),
          needs_pr_overdue: Number(prWork?.needs_pr_overdue ?? 0),
          partial_pr: Number(prWork?.partial_pr ?? 0),
          partial_pr_today: Number(prWork?.partial_pr_today ?? 0),
          partial_pr_overdue: Number(prWork?.partial_pr_overdue ?? 0),
          pending_approval: Number(prWork?.pending_approval ?? 0),
          pending_approval_today: Number(prWork?.pending_approval_today ?? 0),
          pending_approval_overdue: Number(
            prWork?.pending_approval_overdue ?? 0,
          ),
          all_pending: Number(
            prWork?.all_pending ?? EMPTY_PR_WORK_SUMMARY.all_pending,
          ),
        },
        po_work: {
          overdue: Number(poWork?.overdue ?? 0),
          today: Number(poWork?.today ?? 0),
          needs_po: Number(poWork?.needs_po ?? 0),
          needs_po_today: Number(poWork?.needs_po_today ?? 0),
          needs_po_overdue: Number(poWork?.needs_po_overdue ?? 0),
          partial_po: Number(poWork?.partial_po ?? 0),
          partial_po_today: Number(poWork?.partial_po_today ?? 0),
          partial_po_overdue: Number(poWork?.partial_po_overdue ?? 0),
          email_to_vendor: Number(poWork?.email_to_vendor ?? 0),
          email_to_vendor_today: Number(poWork?.email_to_vendor_today ?? 0),
          email_to_vendor_overdue: Number(poWork?.email_to_vendor_overdue ?? 0),
          all_pending: Number(
            poWork?.all_pending ?? EMPTY_PO_WORK_SUMMARY.all_pending,
          ),
        },
        raw_work: {
          overdue: Number(rawWork?.overdue ?? 0),
          today: Number(rawWork?.today ?? 0),
          needs_inward: Number(rawWork?.needs_inward ?? 0),
          needs_inward_today: Number(rawWork?.needs_inward_today ?? 0),
          needs_inward_overdue: Number(rawWork?.needs_inward_overdue ?? 0),
          partial_inward: Number(rawWork?.partial_inward ?? 0),
          partial_inward_today: Number(rawWork?.partial_inward_today ?? 0),
          partial_inward_overdue: Number(rawWork?.partial_inward_overdue ?? 0),
          needs_qc: Number(rawWork?.needs_qc ?? 0),
          needs_qc_today: Number(rawWork?.needs_qc_today ?? 0),
          needs_qc_overdue: Number(rawWork?.needs_qc_overdue ?? 0),
          qc_in_progress: Number(rawWork?.qc_in_progress ?? 0),
          qc_in_progress_today: Number(rawWork?.qc_in_progress_today ?? 0),
          qc_in_progress_overdue: Number(rawWork?.qc_in_progress_overdue ?? 0),
          qc_on_hold: Number(rawWork?.qc_on_hold ?? 0),
          qc_on_hold_today: Number(rawWork?.qc_on_hold_today ?? 0),
          qc_on_hold_overdue: Number(rawWork?.qc_on_hold_overdue ?? 0),
          all_pending: Number(
            rawWork?.all_pending ?? EMPTY_RAW_WORK_SUMMARY.all_pending,
          ),
        },
        print_work: {
          overdue: Number(printWork?.overdue ?? 0),
          today: Number(printWork?.today ?? 0),
          needs_outward: Number(printWork?.needs_outward ?? 0),
          needs_outward_today: Number(printWork?.needs_outward_today ?? 0),
          needs_outward_overdue: Number(printWork?.needs_outward_overdue ?? 0),
          partial_outward: Number(printWork?.partial_outward ?? 0),
          partial_outward_today: Number(printWork?.partial_outward_today ?? 0),
          partial_outward_overdue: Number(
            printWork?.partial_outward_overdue ?? 0,
          ),
          all_pending: Number(
            printWork?.all_pending ?? EMPTY_PRINT_WORK_SUMMARY.all_pending,
          ),
        },
        print_in_work: {
          overdue: Number(printInWork?.overdue ?? 0),
          today: Number(printInWork?.today ?? 0),
          needs_print_in: Number(printInWork?.needs_print_in ?? 0),
          needs_print_in_today: Number(printInWork?.needs_print_in_today ?? 0),
          needs_print_in_overdue: Number(
            printInWork?.needs_print_in_overdue ?? 0,
          ),
          partial_print_in: Number(printInWork?.partial_print_in ?? 0),
          partial_print_in_today: Number(
            printInWork?.partial_print_in_today ?? 0,
          ),
          partial_print_in_overdue: Number(
            printInWork?.partial_print_in_overdue ?? 0,
          ),
          needs_print_qc: Number(printInWork?.needs_print_qc ?? 0),
          needs_print_qc_today: Number(printInWork?.needs_print_qc_today ?? 0),
          needs_print_qc_overdue: Number(
            printInWork?.needs_print_qc_overdue ?? 0,
          ),
          print_qc_in_progress: Number(printInWork?.print_qc_in_progress ?? 0),
          print_qc_in_progress_today: Number(
            printInWork?.print_qc_in_progress_today ?? 0,
          ),
          print_qc_in_progress_overdue: Number(
            printInWork?.print_qc_in_progress_overdue ?? 0,
          ),
          print_qc_on_hold: Number(printInWork?.print_qc_on_hold ?? 0),
          print_qc_on_hold_today: Number(
            printInWork?.print_qc_on_hold_today ?? 0,
          ),
          print_qc_on_hold_overdue: Number(
            printInWork?.print_qc_on_hold_overdue ?? 0,
          ),
          all_pending: Number(
            printInWork?.all_pending ?? EMPTY_PRINT_IN_WORK_SUMMARY.all_pending,
          ),
        },
        project_work: {
          overdue: Number(projectWork?.overdue ?? 0),
          today: Number(projectWork?.today ?? 0),
          needs_request: Number(projectWork?.needs_request ?? 0),
          needs_request_today: Number(projectWork?.needs_request_today ?? 0),
          needs_request_overdue: Number(
            projectWork?.needs_request_overdue ?? 0,
          ),
          awaiting_finance: Number(projectWork?.awaiting_finance ?? 0),
          awaiting_finance_today: Number(
            projectWork?.awaiting_finance_today ?? 0,
          ),
          awaiting_finance_overdue: Number(
            projectWork?.awaiting_finance_overdue ?? 0,
          ),
          variants_incomplete: Number(projectWork?.variants_incomplete ?? 0),
          variants_incomplete_today: Number(
            projectWork?.variants_incomplete_today ?? 0,
          ),
          variants_incomplete_overdue: Number(
            projectWork?.variants_incomplete_overdue ?? 0,
          ),
          all_pending: Number(projectWork?.all_pending ?? 0),
        },
      };
    } catch (error) {
      throw error;
    }
  };

  getQueue = async (
    filters: DailyTasksFilters,
  ): Promise<DailyTasksPaginatedList> => {
    try {
      const query = toDailyTasksQueueSearchParams(filters).toString();
      const response = await getAnalyticsAPI(
        `/v1/dashboards/daily-tasks?${query}`,
      );
      if (!response.success) {
        throw new Error(
          response.message || "Failed to fetch daily tasks queue.",
        );
      }
      const result = (response.result ??
        {}) as Partial<DailyTasksPaginatedList>;
      return {
        page_no: Number(result.page_no ?? filters.page_no),
        total_pages: Number(result.total_pages ?? 0),
        total_count: Number(result.total_count ?? 0),
        limit: Number(result.limit ?? filters.limit),
        list: Array.isArray(result.list) ? result.list : [],
      };
    } catch (error) {
      throw error;
    }
  };

  getDetail = async (
    orderId: number,
    productId: number,
    stage?: DailyTasksStage,
  ): Promise<DailyTasksDetailResult> => {
    try {
      const queryParams = new URLSearchParams();
      if (stage) queryParams.append("stage", stage);
      const suffix = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const response = await getAnalyticsAPI(
        `/v1/dashboards/daily-tasks/${orderId}/products/${productId}${suffix}`,
      );
      if (!response.success || !response.result) {
        throw new Error(
          response.message || "Failed to fetch daily tasks product detail.",
        );
      }
      return response.result as DailyTasksDetailResult;
    } catch (error) {
      throw error;
    }
  };
}
